export function dragOverflow({
	                             wrapper,
	                             inner,
	                             mouseHover = false,
	                             inertia = true,
	                             reset = false,
                             } = {}) {

	/* =========================================================
	   Normalize wrapper → always an array
	========================================================== */
	const wrappers =
		      wrapper instanceof NodeList || Array.isArray(wrapper)
			      ? Array.from(wrapper)
			      : [wrapper];

	if (!wrappers.length || !inner)
	{
		console.warn("dragOverflow: 'wrapper' and 'inner' are required");
		return;
	}

	// Respect reduced motion preference
	if (matchMedia("(prefers-reduced-motion: reduce)").matches)
	{
		return;
	}

	/* =========================================================
	   Initialize one independent instance per wrapper
	========================================================== */
	wrappers.forEach(wrapper => {
		if (!wrapper)
		{
			return;
		}

		// inner is always a descendant of wrapper
		const innerEl =
			      typeof inner === "string"
				      ? wrapper.querySelector(inner)
				      : inner;

		if (!innerEl)
		{
			return;
		}

		/* =======================================================
		   Configuration (per instance)
		======================================================== */
		const EASING          = 0.05;  // smooth follow
		const FRICTION        = 0.96;  // inertia decay
		const RESET_DELAY     = 800;   // idle reset delay
		const EDGE_THRESHOLD  = 1;     // fade tolerance
		const VELOCITY_WINDOW = 100;   // ms

		/* =======================================================
		   State (per instance)
		======================================================== */
		let current     = 0;  // actual rendered position
		let hoverTarget = 0;  // destination for hover easing
		let velocity    = 0;
		let max         = 0;

		let isDragging     = false;
		let pointerId      = null;
		let dragOffset     = 0;   // current + startX, pre-computed on pointerdown
		let pointerHistory = [];
		let idleTimer      = null;

		/* =======================================================
		   Setup
		======================================================== */

		// Allow vertical scroll while handling horizontal drag
		const computedTouchAction = getComputedStyle(wrapper).touchAction;

		// Only force pan-y if the current value could block vertical scrolling
		if (!computedTouchAction.includes("pan-y") && computedTouchAction !== "manipulation")
		{
			wrapper.style.touchAction = "pan-y";
		}

		/* =======================================================
		   Utilities
		======================================================== */
		const clamp = v => Math.max(0, Math.min(max, v));

		// Compute max horizontal overflow
		const updateBounds = () => {
			max = Math.max(0, innerEl.scrollWidth - wrapper.offsetWidth);
		};

		// Toggle gradient fade classes
		const updateFadeState = () => {
			wrapper.classList.toggle("has-left-overflow", current > EDGE_THRESHOLD);
			wrapper.classList.toggle("has-right-overflow", current < max - EDGE_THRESHOLD);
		};

		// Schedule reset to start (if enabled)
		const scheduleReset = () => {
			if (!reset)
			{
				return;
			}
			clearTimeout(idleTimer);
			idleTimer = setTimeout(() => {
				velocity    = 0;
				hoverTarget = 0;
			}, RESET_DELAY);
		};

		const cancelReset = () => clearTimeout(idleTimer);

		// Map mouse position (10% → 90%) to scroll.
		// The 10/90 dead zones prevent the content from reaching its
		// extremes only when the cursor is literally at the wrapper edge.
		const mapHover = clientX => {
			const {left, width} = wrapper.getBoundingClientRect();
			const t             = Math.max(0, Math.min(1, ((clientX - left) / width - 0.1) / 0.8));
			hoverTarget         = max * t;
		};

		/* =======================================================
		   Animation Loop
		======================================================== */
		const animate = () => {
			if (isDragging)
			{
				// Finger is down — current is written directly in pointermove
			}
			else if (inertia && Math.abs(velocity) >= 0.02)
			{
				// Inertia phase — velocity drives current directly.
				// No easing on top: FRICTION is the only smoothing needed here.
				// Each frame, velocity is multiplied by FRICTION (< 1), so it
				// decays exponentially and current naturally glides to a stop.

				current = clamp(current + velocity);
				velocity *= FRICTION;
			}
			else if (mouseHover)
			{
				// Idle / hover — ease current toward hover target.
				// Moves 5% of the remaining distance each frame, which produces
				// a smooth deceleration without overshooting.

				velocity = 0;
				current += (clamp(hoverTarget) - current) * EASING;
			}
			else
			{
				velocity = 0;
			}

			// Apply transform
			innerEl.style.transform = `translateX(${-current}px)`;
			// Update gradient fades
			updateFadeState();
			requestAnimationFrame(animate);
		};

		/* =======================================================
		   Pointer Events (mouse + touch unified)
		======================================================== */
		wrapper.addEventListener("pointerdown", e => {
			if (!max)
			{
				return;
			}
			// Disable mouse drag when hover mode is active
			if (mouseHover && e.pointerType === "mouse")
			{
				return;
			}

			cancelReset();

			isDragging = true;
			pointerId  = e.pointerId;
			velocity   = 0;

			// Pre-compute the offset between the grab point and the current scroll
			// position. This lets pointermove resolve current in a single operation:
			// current = dragOffset - x  →  (startCurrent + startX) - x
			//                           →  startCurrent + (startX - x)

			dragOffset = current + e.clientX;

			pointerHistory = [{x: e.clientX, t: performance.now()}];
			wrapper.setPointerCapture?.(pointerId);
		});

		wrapper.addEventListener("pointermove", e => {
			// ===== Dragging =====
			if (isDragging && e.pointerId === pointerId)
			{
				const x   = e.clientX;
				const now = performance.now();

				// Drive current from the visual start position
				current = clamp(dragOffset - x);

				if (inertia)
				{
					pointerHistory.push({x, t: now});

					// Drop samples older than VELOCITY_WINDOW so only recent
					// motion contributes to the flick velocity on release.
					// shift() from the front is cheaper than filter() because
					// it mutates in place instead of allocating a new array.

					while (pointerHistory.length > 1 && now - pointerHistory[0].t > VELOCITY_WINDOW)
					{
						pointerHistory.shift();
					}
				}
				return;
			}

			// ===== Mouse hover mode =====
			if (mouseHover && e.pointerType === "mouse" && !isDragging)
			{
				if (!max)
				{
					return;
				}
				cancelReset();
				mapHover(e.clientX);
			}
		});

		const endDrag = e => {
			if (!isDragging || e.pointerId !== pointerId)
			{
				return;
			}

			isDragging = false;
			pointerId  = null;

			if (inertia && pointerHistory.length >= 2)
			{
				const oldest = pointerHistory[0];
				const newest = pointerHistory[pointerHistory.length - 1];
				const dt     = newest.t - oldest.t;

				if (dt > 0)
				{
					// Compute velocity from the oldest sample still inside the window
					// rather than the last two consecutive events. Consecutive events
					// fire so rapidly that the delta approaches zero just before the
					// finger lifts, which would kill the flick. Sampling across the
					// full window captures the true gesture speed.
					// Convert from px/ms to px/frame, assuming 60 fps (≈16 ms/frame).

					velocity = ((oldest.x - newest.x) / dt) * 16;
				}
			}

			pointerHistory = [];
			scheduleReset();
		};

		wrapper.addEventListener("pointerup", endDrag);
		wrapper.addEventListener("pointercancel", endDrag);

		// Reset when mouse leaves (hover mode only)
		wrapper.addEventListener("pointerleave", e => {
			if (mouseHover && e.pointerType === "mouse" && !isDragging)
			{
				scheduleReset();
			}
		});

		/* =======================================================
		   Init
		======================================================== */
		window.addEventListener("resize", updateBounds);
		updateBounds();
		animate();
	});
}